import React from 'react';
import {
  formatAiReportFieldLabel,
  getAiReportTypeConfig,
  normalizeAiReportParamInputType,
  normalizeAiReportParamOptions,
} from '../utils/aiReportTypes';
import { modeIncludesAiReport } from '../utils/reportDownload';

const isTruthy = (value) => value === true || value === 'true' || value === '1' || value === 1;

const toggleListValue = (currentValue, optionValue) => {
  const current = Array.isArray(currentValue)
    ? currentValue
    : String(currentValue || '')
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);

  return current.includes(optionValue)
    ? current.filter((entry) => entry !== optionValue)
    : [...current, optionValue];
};

const AiReportVariablesStep = ({
  reportMode,
  reportType,
  types = [],
  variables,
  onVariableChange,
  missingFields = [],
  showValidation = false,
  labels = {},
}) => {
  if (!modeIncludesAiReport(reportMode)) {
    return null;
  }

  const config = getAiReportTypeConfig(types, reportType);
  const visibleParams = (config?.params || []).filter((param) => param?.system !== true);
  const missingFieldSet = new Set((missingFields || []).map((field) => String(field)));
  const renderParamInput = (param) => {
    const inputType = normalizeAiReportParamInputType(param.inputType);
    const options = normalizeAiReportParamOptions(param.options);
    const placeholder = param.placeholder || param.label || formatAiReportFieldLabel(param.key);
    const value = variables?.[param.key];

    if (inputType === 'number') {
      return (
        <input
          type="number"
          className="dr-input"
          value={value ?? ''}
          onChange={(event) => onVariableChange?.(param.key, event.target.value)}
          placeholder={placeholder}
        />
      );
    }

    if (inputType === 'textarea') {
      return (
        <textarea
          className="dr-input dr-textarea"
          value={value ?? ''}
          onChange={(event) => onVariableChange?.(param.key, event.target.value)}
          placeholder={placeholder}
        />
      );
    }

    if (inputType === 'switch') {
      return (
        <button
          type="button"
          className={`dr-aiVars-switch ${isTruthy(value) ? 'is-on' : ''}`}
          onClick={() => onVariableChange?.(param.key, !isTruthy(value))}
          aria-pressed={isTruthy(value)}
        >
          <span />
          <strong>{isTruthy(value) ? 'On' : 'Off'}</strong>
        </button>
      );
    }

    if (inputType === 'select' && options.length > 0) {
      return (
        <select
          className="dr-input"
          value={value ?? ''}
          onChange={(event) => onVariableChange?.(param.key, event.target.value)}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (inputType === 'checkbox' && options.length > 0) {
      const selected = Array.isArray(value)
        ? value
        : String(value || '')
            .split(',')
            .map((entry) => entry.trim())
            .filter(Boolean);

      return (
        <div className="dr-aiVars-options">
          {options.map((option) => (
            <label key={option.value} className="dr-aiVars-option">
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => onVariableChange?.(param.key, toggleListValue(selected, option.value))}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      );
    }

    return (
      <input
        type="text"
        className="dr-input"
        value={value ?? ''}
        onChange={(event) => onVariableChange?.(param.key, event.target.value)}
        placeholder={placeholder}
      />
    );
  };

  return (
    <div className="dr-aiVars">
      <div className="dr-aiVars-head">
        <span className="dr-aiVars-step">{labels.step || 'Step 2'}</span>
        <div>
          <h3>{labels.title || 'Fill AI report variables'}</h3>
          <p>
            {config
              ? labels.description || 'These values are sent with the CSV to the AI report API for this type.'
              : labels.missingType || 'Choose a type on the template or dashboard before generating an AI report.'}
          </p>
        </div>
      </div>

      {config ? (
        <div className="dr-aiVars-grid">
          {visibleParams.map((param) => {
            const isRequired = param.required !== false;
            const hasError = showValidation && missingFieldSet.has(param.key);

            return (
              <div key={param.key} className={`dr-aiVars-field${hasError ? ' has-error' : ''}`}>
                <div className="dr-aiVars-labelRow">
                  <span>{param.label || formatAiReportFieldLabel(param.key)}</span>
                  <em className={`dr-aiVars-requirement ${isRequired ? 'is-required' : 'is-optional'}`}>
                    {isRequired ? labels.required || 'Required' : labels.optional || 'Optional'}
                  </em>
                </div>
                {renderParamInput(param)}
                {hasError ? (
                  <small className="dr-aiVars-error">
                    {labels.requiredFieldError || 'This field is required.'}
                  </small>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default AiReportVariablesStep;
